import { type CreditNoteListRow, type CreditNoteStatus } from '@utazzo/shared';
import { UzBadge, UzButton, UzCard, UzSelect } from '@utazzo/ui';
import { Download } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { FyRangeControls, useFyRange } from '../components/FyRangeControls';
import { formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useCreditNotesList } from '../use-invoices';

import { downloadExcel, type CsvColumn } from '@/components/list-screen/list-screen-utils';
import { PageShell } from '@/components/page-shell';

/**
 * Global credit-note listing (route /reports/credit-notes). A single-fetch
 * finance report: a date-range + status filter bar, a plain table of every
 * credit note raised in the period and a total (sum of ISSUED notes) summary
 * strip. Each row links to its invoice and opens its print sheet in a new tab.
 */

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ISSUED', label: 'Issued' },
  { value: 'USED', label: 'Used' },
  { value: 'CANCELLED', label: 'Cancelled' },
] as const;

type StatusFilter = '' | CreditNoteStatus;

const KIND_LABEL: Record<string, string> = { FULL: 'Full', PARTIAL: 'Partial' };

/** Rupees (number, for spreadsheet math) from a signed paise string. */
const rupees = (paise: string): number => Number(paise) / 100;

/** Full-detail export columns for the credit-note listing. */
const EXPORT_COLUMNS: CsvColumn<CreditNoteListRow>[] = [
  { header: 'Credit note no.', value: (r) => r.creditNoteNumber },
  { header: 'Date', value: (r) => formatDateDMY(r.issuedAt) },
  { header: 'Invoice no.', value: (r) => r.invoiceNumber },
  { header: 'Customer', value: (r) => r.customerName ?? '' },
  { header: 'Kind', value: (r) => KIND_LABEL[r.kind] ?? r.kind },
  { header: 'Status', value: (r) => r.status },
  { header: 'Reason', value: (r) => r.reason },
  { header: 'Taxable credited', value: (r) => rupees(r.creditedTaxablePaise) },
  { header: 'CGST reversed', value: (r) => rupees(r.reversedCgstPaise) },
  { header: 'SGST reversed', value: (r) => rupees(r.reversedSgstPaise) },
  { header: 'IGST reversed', value: (r) => rupees(r.reversedIgstPaise) },
  { header: 'Total', value: (r) => rupees(r.totalPaise) },
  { header: 'Used at', value: (r) => (r.usedAt ? formatDateDMY(r.usedAt) : '') },
  { header: 'Used note', value: (r) => r.usedNote ?? '' },
];

const CreditNoteStatusBadge = ({ status }: { status: CreditNoteStatus }): React.JSX.Element => (
  <UzBadge
    tone={status === 'ISSUED' ? 'success' : status === 'USED' ? 'warning' : 'destructive'}
    pill
  >
    {status === 'ISSUED' ? 'Issued' : status === 'USED' ? 'Used' : 'Cancelled'}
  </UzBadge>
);

export const CreditNotesListPage = (): React.JSX.Element => {
  const fyState = useFyRange();
  const { from, to } = fyState;
  const [status, setStatus] = useState<StatusFilter>('');

  const reportQuery = useCreditNotesList({
    from,
    to,
    ...(status ? { status } : {}),
  });

  const rows = reportQuery.data?.rows ?? [];

  return (
    <PageShell
      title="Credit notes"
      description="Credit notes raised against tax invoices for a period."
      actions={
        <UzButton
          variant="outline"
          leftIcon={<Download className="h-4 w-4" />}
          disabled={rows.length === 0}
          onClick={() => downloadExcel(rows, EXPORT_COLUMNS, 'credit-notes')}
        >
          Export Excel
        </UzButton>
      }
    >
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <FyRangeControls state={fyState} />
        <UzSelect
          label="Status"
          value={status}
          onValueChange={(v) => setStatus(v as StatusFilter)}
          options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          containerClassName="w-44"
        />
        <UzButton variant="outline" onClick={() => void reportQuery.refetch()}>
          Refresh
        </UzButton>
      </div>

      <UzCard padding="none">
        {reportQuery.isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Loading credit notes...</p>
        ) : reportQuery.isError ? (
          <p className="p-6 text-sm text-red-600">Could not load the credit notes.</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">No credit notes in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                  <th className="px-4 py-3">Credit note no.</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Invoice no.</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Kind</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Print</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-zinc-100 last:border-0">
                    <td className="px-4 py-3 whitespace-nowrap text-zinc-700 tabular-nums">
                      {row.creditNoteNumber}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-zinc-700 tabular-nums">
                      {formatDateDMY(row.issuedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap tabular-nums">
                      <Link
                        to={`/invoices/${row.invoiceId}`}
                        className="text-(--primary) hover:underline"
                      >
                        {row.invoiceNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{row.customerName ?? '-'}</td>
                    <td className="px-4 py-3 text-zinc-700">{KIND_LABEL[row.kind] ?? row.kind}</td>
                    <td className="px-4 py-3 text-zinc-700">{row.reason || '-'}</td>
                    <td className="px-4 py-3 text-right font-medium whitespace-nowrap text-zinc-900 tabular-nums">
                      {formatInrPaise(row.totalPaise)}
                    </td>
                    <td className="px-4 py-3">
                      <CreditNoteStatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <a
                        href={`/invoices/credit-notes/${row.id}/print`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-(--primary) hover:underline"
                      >
                        Print
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-900">
                  <td className="px-4 py-3" colSpan={6}>
                    Total (issued)
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap tabular-nums">
                    {formatInrPaise(reportQuery.data?.totalPaise ?? '0')}
                  </td>
                  <td className="px-4 py-3" colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </UzCard>
    </PageShell>
  );
};
